/* Team & Department — the factual rollup screen (#/team, rail item 03),
 * Package 5.
 *
 * A frontend port of the owner's approved export (crm-hub/team.html, S10) +
 * anatomy §2.6: a MODEST FACTUAL rollup — per-member workload counts (open ·
 * waiting · overdue · verified) and client coverage, derived from the SAME task
 * ledger My Work reads. NOT org-chart art, NOT a priority engine.
 *
 * Data path (build brief): GET /users (the real roster + personas) + the
 * per-client task aggregation (lib/rosterTasks.ts), rolled up by
 * assignee_user_id (lib/team.ts). ONLY real /users rows are members — no
 * fabricated people. Whatever authorization the backend enforces on /users is
 * respected as-is (a 401/403 surfaces honestly); the client invents no authz.
 *
 * Honest absence: team STRUCTURE (teams, leads, reporting lines), CAPACITY and
 * UTILIZATION are not derivable from the backend, so they render an explicit
 * "not tracked" note rather than invented org-chart art. Members are grouped by
 * the one structure the backend DOES carry — persona. A member with no ledger
 * tasks reads "no indexed work", never a fabricated healthy zero.
 */
import { html, nothing, render, type TemplateResult } from 'lit';
import { ApiError } from '../api/client.ts';
import { getUsers } from '../api/users.ts';
import type { AuthUser } from '../lib/auth.ts';
import { personInitials } from '../lib/identity.ts';
import { capacityLabel } from '../lib/personas.ts';
import { fetchRosterTasks } from '../lib/rosterTasks.ts';
import { ledgerTotals, rollupMembers, type LedgerTotals, type MemberRollup } from '../lib/team.ts';

let members: MemberRollup[] = [];
let ledger: LedgerTotals | null = null;
let brandBySlug = new Map<string, string>();
let loaded = false;
let errored = false;
let errorMsg = '';

/** Persona group label for a user's primary_persona (or an honest fallback).
 * Management capacities keep capacityLabel's role wording (CEO / head of X). */
function personaLabel(u: AuthUser): string {
  const p = u.primary_persona;
  if (!p) return 'No persona assigned';
  switch (p) {
    case 'am': return 'Account management';
    case 'bizdev': return 'Business development';
    case 'writer': return 'Content & writing';
    default: {
      const label = capacityLabel(p); // management:<dept> → "CEO" / "head of <dept>"
      return label.charAt(0).toUpperCase() + label.slice(1);
    }
  }
}

function headView(): TemplateResult {
  return html`<div class="portfolio-head">
    <div>
      <span class="eyebrow">03 · team &amp; department · factual rollup</span>
      <h1>Team</h1>
      <p>Assignment and workload rollups resolved from the one task ledger — not a generated priority plan,
        and never authorized by job title. Health rolls up only across indexed clients.</p>
    </div>
  </div>`;
}

function summaryView(): TemplateResult {
  // FIX-audit F14: the dept-wide cells report the WHOLE roster ledger, not the
  // per-member (linked-only) sum. Legacy assignees are free-text names with no
  // user id, so a member-summed total reads "0 open · nothing open" while the
  // book carries dozens of open tasks — a false negative. `linkedOpen` states
  // the linkage gap honestly instead of hiding the work.
  const t = ledger ?? { members: members.length, clientsCovered: 0, open: 0, waiting: 0, overdue: 0, verified: 0, linkedOpen: 0, unlinkedOpen: 0 };
  const cell = (label: string, value: number, sub: string): TemplateResult => html`
    <div class="summary-cell"><span>${label}</span><strong>${value}</strong><small>${sub}</small></div>`;
  const openSub = t.open
    ? (t.unlinkedOpen
      ? `${t.linkedOpen} linked · ${t.unlinkedOpen} not yet linked to users`
      : 'all linked to a member')
    : 'nothing open';
  return html`<div class="portfolio-summary" aria-label="Department rollup">
    ${cell('Members', t.members, t.members === 1 ? 'real /users row' : 'real /users rows')}
    ${cell('Clients covered', t.clientsCovered, t.clientsCovered ? 'in the ledger' : 'none held')}
    ${cell('Open · dept-wide', t.open, openSub)}
    ${cell('Waiting on external', t.waiting, t.waiting ? 'editor / platform / client' : 'nothing waiting')}
    ${cell('Overdue', t.overdue, t.overdue ? 'past a connected due date' : 'none past due')}
  </div>`;
}

function notTrackedNote(): TemplateResult {
  const gap = ledger && ledger.unlinkedOpen > 0
    ? html`<p class="section-note">${ledger.unlinkedOpen} of ${ledger.open} open task${ledger.open === 1 ? '' : 's'} are
      assigned to legacy name-strings <strong>not yet linked to registered users</strong> — counted in the dept-wide total
      above but not attributable to a member row below (which is why a member can read “no indexed work” while the book is busy).</p>`
    : nothing;
  return html`<p class="section-note">Team structure, reporting lines, capacity and utilization are
    <strong>not tracked</strong> — the backend carries no org chart. Members are grouped by persona, the one
    structure it does record. A client with no task ledger reads “record only”, never as red.</p>${gap}`;
}

function healthChips(m: MemberRollup): TemplateResult {
  if (m.noWork) return html`<div class="health-chips"><span class="hc muted">no indexed work</span></div>`;
  const coverage = m.clients.map((s) => brandBySlug.get(s) || s).join(', ');
  return html`<div class="health-chips">
    <span class="hc">${m.open} open</span>
    ${m.waiting ? html`<span class="hc waiting">${m.waiting} waiting</span>` : nothing}
    ${m.overdue ? html`<span class="hc overdue">${m.overdue} overdue</span>` : nothing}
    ${m.verified ? html`<span class="hc">${m.verified} verified</span>` : nothing}
    ${coverage ? html`<span class="hc muted">${coverage}</span>` : nothing}
  </div>`;
}

function memberRow(m: MemberRollup): TemplateResult {
  return html`<div class="am-row">
    <span class="avatar avatar-person sm">${personInitials(m.user.name)}</span>
    <div class="am-name"><strong>${m.user.name}</strong><small>${m.user.email}</small></div>
    ${healthChips(m)}
  </div>`;
}

function groupCard(label: string, group: MemberRollup[]): TemplateResult {
  const active = group.some((m) => !m.noWork);
  return html`<article class="team-card">
    <div class="team-head">
      <div><span class="eyebrow">${label}</span><h2>${group.length} member${group.length === 1 ? '' : 's'}</h2></div>
      <span class="status-label ${active ? 'calm' : ''}">${active ? 'Active in the ledger' : 'No indexed work'}</span>
    </div>
    ${group.map(memberRow)}
  </article>`;
}

function view(): TemplateResult {
  if (!loaded && !errored) {
    return html`<div class="register-empty" style="padding:var(--sp-5);color:var(--crm-tertiary);font-size:.78rem">Loading the department rollup…</div>`;
  }
  if (errored) {
    return html`<div class="register-empty" style="padding:var(--sp-5);color:var(--crm-tertiary);font-size:.78rem">${errorMsg
      || 'Workspace service unreachable — start the service and reload.'}</div>`;
  }
  if (!members.length) {
    return html`${headView()}<div class="register-empty" style="padding:var(--sp-5);color:var(--crm-tertiary);font-size:.78rem">No users on record yet.</div>`;
  }
  // Group by persona (the only backend-recorded structure), stable by label.
  const groups = new Map<string, MemberRollup[]>();
  for (const m of members) {
    const label = personaLabel(m.user);
    const arr = groups.get(label);
    if (arr) arr.push(m); else groups.set(label, [m]);
  }
  const ordered = [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  return html`
    ${headView()}
    ${summaryView()}
    ${notTrackedNote()}
    <div class="team-grid">${ordered.map(([label, group]) => groupCard(label, group))}</div>`;
}

function paint(): void {
  const host = document.getElementById('teamView');
  if (host) render(view(), host);
}

/** Fetch the roster + the per-client task aggregation and paint the rollup.
 * Called on navigation into #/team (and on a direct arrival). A 401/403 from
 * /users surfaces honestly — the client enforces no authz of its own. */
export async function initTeam(): Promise<void> {
  const host = document.getElementById('teamView');
  if (!host) return;
  if (!loaded) paint();
  try {
    const [users, roster] = await Promise.all([getUsers(), fetchRosterTasks()]);
    brandBySlug = new Map(roster.clients.map((c) => [c.slug, c.brand]));
    members = rollupMembers(users, roster.tasks);
    ledger = ledgerTotals(users, roster.tasks);
    loaded = true;
    errored = false;
    paint();
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return; // gate is showing
    errored = true;
    errorMsg = e instanceof ApiError && e.status === 403
      ? 'You don’t have access to the team rollup.'
      : '';
    loaded = true;
    paint();
  }
}
